import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card, Tabs, Table, Button, Space, Tag, Switch, InputNumber, Select, Form,
  message, Popconfirm, Popover, Input, Typography, Descriptions, Skeleton, Alert, Divider,
  Row, Col, Statistic, Empty,
} from 'antd'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import { api, panelApi } from '../services/api'
import type { PendingOrderInfo, OpenOrderInfo } from '../types/api'
import { BellOutlined } from '@ant-design/icons'
import { useIsMobile } from '../hooks/useBreakpoint'
import { usePanelSessionRefresh } from '../hooks/usePanelSessionRefresh'
import { usePanelOrderNotifications } from '../hooks/usePanelOrderNotifications'
import { getNotificationPermission, getPendingNotifyEnabled } from '../utils/browserNotification'
import NewsFlashTab from '../components/news/NewsFlashTab'
import MarketSentimentTab from '../components/market/MarketSentimentTab'
import MobileOrderTabBar from '../components/MobileOrderTabBar'

const { Title, Text } = Typography

const BLOCK_COEFFICIENT_MIN = 0
const BLOCK_COEFFICIENT_MAX = 2

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

const formatPnl = (value: unknown): string => {
  const n = toNumber(value, NaN)
  if (!Number.isFinite(n)) return '-'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(2)}`
}

const formatPnlUsd = (value: unknown): string => {
  const text = formatPnl(value)
  return text === '-' ? '-' : `$${text}`
}

const pnlColor = (value: unknown): string => {
  const n = toNumber(value, 0)
  return n >= 0 ? '#3f8600' : '#cf1322'
}

/** 移动端缩短交易对显示，如 ADA/USDT:USDT → ADA/USDT */
const formatSymbolShort = (symbol: unknown): string => {
  const s = String(symbol || '').trim()
  if (!s) return '-'
  return s.replace(/:USDT$/i, '').replace(/:USD$/i, '')
}

const sideLabel = (side: unknown): string => {
  const s = String(side || '').toLowerCase()
  if (s === 'buy' || s === 'long') return '买'
  if (s === 'sell' || s === 'short') return '卖'
  return s || '-'
}

const EXEC_MODE_OPTIONS = [
  { value: 'off', label: '关闭（普通市价）' },
  { value: 'maker_market', label: '市价转限价（maker_market）' },
  { value: 'limit_offset', label: '偏移百分比限价（limit_offset）' },
]

const STATUS_COLOR: Record<string, string> = {
  pending: 'orange', released: 'blue', canceled: 'default',
  executed: 'green', failed: 'red',
}

type AuthMode = 'admin' | 'panel' | 'none'

const pageWrap: React.CSSProperties = {
  padding: '8px',
  maxWidth: 1200,
  margin: '0 auto',
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
  overflowX: 'hidden',
}

function detectAuthMode(tokenId: number): AuthMode {
  const adminToken = localStorage.getItem('token')
  if (adminToken) return 'admin'
  const pt = localStorage.getItem('panel_token')
  const ptid = Number(localStorage.getItem('panel_token_id'))
  if (pt && ptid === tokenId) return 'panel'
  return 'none'
}

export default function TokenOrders() {
  const { id } = useParams<{ id: string }>()
  const tokenId = Number(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [configForm] = Form.useForm()
  const [includeClosed, setIncludeClosed] = useState(false)
  const [activeTab, setActiveTab] = useState('positions')
  const [pollNotifyInBackground, setPollNotifyInBackground] = useState(
    () => getPendingNotifyEnabled(tokenId) && getNotificationPermission() === 'granted',
  )
  const isMobile = useIsMobile()

  const [authMode, setAuthMode] = useState<AuthMode>(() => detectAuthMode(tokenId))

  usePanelSessionRefresh(authMode === 'panel')

  useEffect(() => {
    const mode = detectAuthMode(tokenId)
    setAuthMode(mode)
    if (mode === 'none') {
      navigate('/panel-login', { replace: true })
    }
  }, [tokenId, navigate])

  // 选择 api 集
  const A = authMode === 'panel' ? panelApi : api.token
  const positionsApi = authMode === 'panel'
    ? (tid: number) => panelApi.getPositions(tid)
    : (_tid: number) => api.trading.getPositions({ token_id: tokenId })
  const eventsApi = authMode === 'panel'
    ? (tid: number) => panelApi.getTradeEvents(tid, 50)
    : (_tid: number) => api.logs.getTradeEvents({ token_id: tokenId, limit: 50 })
  const tokenInfoApi = authMode === 'panel'
    ? (tid: number) => panelApi.getPanelInfo(tid)
    : (tid: number) => api.token.get(tid)
  const configSaveApi = authMode === 'panel'
    ? (tid: number, data: any) => panelApi.updatePanelConfig(tid, data)
    : (tid: number, data: any) => api.token.update(tid, data)

  // token info
  const { data: token, isLoading: tokenLoading } = useQuery<any>({
    queryKey: ['token', tokenId, authMode],
    queryFn: () => tokenInfoApi(tokenId),
    enabled: authMode !== 'none' && !!tokenId,
  })

  const configMutation = useMutation({
    mutationFn: (data: any) => configSaveApi(tokenId, data),
    onSuccess: () => {
      message.success('配置已保存')
      queryClient.invalidateQueries({ queryKey: ['token', tokenId, authMode] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '保存失败'),
  })

  const { data: pendingOrders, isLoading: pendingLoading } = useQuery<PendingOrderInfo[]>({
    queryKey: ['pending-orders', tokenId, includeClosed, authMode],
    queryFn: () => A.getPendingOrders(tokenId, includeClosed),
    enabled: authMode !== 'none' && !!tokenId,
    refetchInterval: 4000,
    refetchIntervalInBackground: pollNotifyInBackground,
  })

  const { data: tradeEvents, isLoading: logLoading } = useQuery<any[]>({
    queryKey: ['token-trade-events', tokenId, authMode],
    queryFn: () => eventsApi(tokenId),
    enabled: authMode !== 'none' && !!tokenId,
    refetchInterval: pollNotifyInBackground ? 4000 : false,
    refetchIntervalInBackground: pollNotifyInBackground,
  })

  const panelNotify = usePanelOrderNotifications({
    tokenId,
    tokenName: token?.name,
    pendingOrders,
    tradeEvents,
    enabled: authMode !== 'none',
    onOpenPendingTab: () => setActiveTab('pending'),
    onOpenPositionsTab: () => setActiveTab('positions'),
  })

  useEffect(() => {
    setPollNotifyInBackground(panelNotify.shouldPollInBackground)
  }, [panelNotify.shouldPollInBackground])

  useEffect(() => {
    setPollNotifyInBackground(
      getPendingNotifyEnabled(tokenId) && getNotificationPermission() === 'granted',
    )
  }, [tokenId])

  const { data: positions, isLoading: posLoading } = useQuery<any[]>({
    queryKey: ['token-positions', tokenId, authMode],
    queryFn: () => positionsApi(tokenId),
    enabled: authMode !== 'none' && !!tokenId,
    refetchInterval: 8000,
    refetchIntervalInBackground: false,
  })

  const { data: openOrders, isLoading: ooLoading } = useQuery<OpenOrderInfo[]>({
    queryKey: ['token-open-orders', tokenId, authMode],
    queryFn: () => A.getOpenOrders(tokenId),
    enabled: authMode !== 'none' && !!tokenId,
    refetchInterval: 8000,
    refetchIntervalInBackground: false,
  })

  const [statsDays, setStatsDays] = useState(7)
  const { data: execStats, isLoading: statsLoading } = useQuery<any>({
    queryKey: ['token-execution-stats', tokenId, authMode, statsDays],
    queryFn: () => A.getExecutionStats(tokenId, statsDays),
    enabled: authMode !== 'none' && !!tokenId,
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  })

  const { data: acctLev } = useQuery<any>({
    queryKey: ['token-account-leverage', tokenId, authMode],
    queryFn: () => A.getAccountLeverage(tokenId),
    enabled: authMode !== 'none' && !!tokenId,
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
  })

  const invalidatePending = () => {
    queryClient.invalidateQueries({ queryKey: ['pending-orders', tokenId, includeClosed, authMode] })
    queryClient.invalidateQueries({ queryKey: ['token-positions', tokenId, authMode] })
  }

  const releaseMutation = useMutation({
    mutationFn: ({ poId, mode }: { poId: number; mode: string }) => A.releasePendingOrder(tokenId, poId, mode),
    onSuccess: () => { message.success('已放行'); invalidatePending() },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '放行失败'),
  })
  const cancelPendingMutation = useMutation({
    mutationFn: (poId: number) => A.cancelPendingOrder(tokenId, poId),
    onSuccess: () => { message.success('已取消'); invalidatePending() },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '取消失败'),
  })
  const setExecPriceMutation = useMutation({
    mutationFn: ({ poId, price }: { poId: number; price: number }) => A.updatePendingOrder(tokenId, poId, { execution_price: price }),
    onSuccess: () => { message.success('已设执行价并挂限价单'); invalidatePending() },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '设置失败'),
  })
  const setCoeffMutation = useMutation({
    mutationFn: ({ poId, coeff }: { poId: number; coeff: number }) => A.updatePendingOrder(tokenId, poId, { coefficient: coeff }),
    onSuccess: () => { message.success('系数已更新'); invalidatePending() },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '更新失败'),
  })
  const cancelOpenOrderMutation = useMutation({
    mutationFn: ({ orderId, symbol }: { orderId: string; symbol: string }) => A.cancelOpenOrder(tokenId, orderId, symbol),
    onSuccess: () => { message.success('已撤单'); queryClient.invalidateQueries({ queryKey: ['token-open-orders', tokenId, authMode] }) },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '撤单失败'),
  })

  // 持仓操作：面板用 panelApi（token 级），管理员用 api.trading（全局）
  const posA = authMode === 'panel'
    ? {
        close: (pid: number, data: any) => panelApi.closePosition(tokenId, pid, data),
        limitClose: (pid: number, data: any) => panelApi.updateLimitClose(tokenId, pid, data),
        stopLoss: (pid: number, data: any) => panelApi.updateStopLoss(tokenId, pid, data),
      }
    : {
        close: (pid: number, data: any) => api.trading.closePosition(pid, data),
        limitClose: (pid: number, data: any) => api.trading.updatePositionLimitClose(pid, data),
        stopLoss: (pid: number, data: any) => api.trading.updatePositionStopLoss(pid, data),
      }
  const invalidatePos = () => queryClient.invalidateQueries({ queryKey: ['token-positions', tokenId, authMode] })
  const closePosMut = useMutation({
    mutationFn: ({ pid, data }: { pid: number; data: any }) => posA.close(pid, data),
    onSuccess: () => { message.success('平仓指令已发送'); invalidatePos() },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '平仓失败'),
  })
  const limitCloseMut = useMutation({
    mutationFn: ({ pid, data }: { pid: number; data: any }) => posA.limitClose(pid, data),
    onSuccess: () => { message.success('限价平仓已更新'); invalidatePos() },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '设置失败'),
  })
  const stopLossMut = useMutation({
    mutationFn: ({ pid, data }: { pid: number; data: any }) => posA.stopLoss(pid, data),
    onSuccess: () => { message.success('止损已更新'); invalidatePos() },
    onError: (e: any) => message.error(e?.response?.data?.detail || e?.message || '设置失败'),
  })

  const execMode = String(token?.execution_mode || 'off').toLowerCase()
  const releaseOptions = [
    { value: 'market', label: '市价放行' },
    ...(execMode === 'maker_market' ? [{ value: 'maker_market', label: '市价转限价放行' }] : []),
    ...(execMode === 'limit_offset' ? [{ value: 'limit_offset', label: '偏移百分比限价放行' }] : []),
  ]

  const logoutPanel = () => {
    localStorage.removeItem('panel_token')
    localStorage.removeItem('panel_token_id')
    navigate('/panel-login', { replace: true })
  }

  const pendingColumns: ColumnsType<PendingOrderInfo> = [
    { title: '交易对', dataIndex: 'symbol', key: 'symbol', width: 110 },
    { title: '方向', dataIndex: 'side', key: 'side', width: 70, render: (s) => <Tag color={s === 'buy' ? 'green' : 'red'}>{s === 'buy' ? '买' : '卖'}</Tag> },
    { title: '意图', dataIndex: 'intent', key: 'intent', width: 70, render: (i) => <Tag color={i === 'open' ? 'blue' : 'orange'}>{i === 'open' ? '开仓' : '平仓'}</Tag> },
    { title: '原始数量', dataIndex: 'original_contracts', key: 'original_contracts', align: 'right' as const, width: 90, render: (v) => String(v) },
    {
      title: '系数', dataIndex: 'coefficient', key: 'coefficient', width: 90, align: 'right' as const,
      render: (v, r) => r.status === 'pending' ? (
        <InputNumber size="small" min={BLOCK_COEFFICIENT_MIN} max={BLOCK_COEFFICIENT_MAX} step={0.01} defaultValue={Number(v)} style={{ width: 80 }}
          onBlur={(e) => {
            const nv = Number((e.target as HTMLInputElement).value)
            if (!Number.isNaN(nv) && nv >= BLOCK_COEFFICIENT_MIN && nv <= BLOCK_COEFFICIENT_MAX && nv !== Number(v)) {
              setCoeffMutation.mutate({ poId: r.id, coeff: nv })
            }
          }} />
      ) : String(v),
    },
    { title: '实际数量', dataIndex: 'effective_contracts', key: 'effective_contracts', align: 'right' as const, width: 90, render: (v) => String(v) },
    { title: '到达市价', dataIndex: 'market_price_at_arrival', key: 'market_price_at_arrival', align: 'right' as const, width: 100, render: (v) => (v != null ? String(v) : '-') },
    { title: '当前价', dataIndex: 'current_price', key: 'current_price', align: 'right' as const, width: 100, render: (v) => (v != null ? String(v) : '-') },
    { title: '距离%', dataIndex: 'distance_pct', key: 'distance_pct', align: 'right' as const, width: 90, render: (v) => (v != null ? <span style={{ color: v >= 0 ? '#cf1322' : '#3f8600' }}>{v.toFixed(3)}%</span> : '-') },
    { title: '执行价', dataIndex: 'execution_price', key: 'execution_price', align: 'right' as const, width: 100, render: (v) => (v != null ? String(v) : '-') },
    { title: '状态', dataIndex: 'status', key: 'status', width: 90, render: (s) => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag> },
    {
      title: '操作', key: 'actions', width: 220, fixed: 'right' as const,
      render: (_, r) => r.status === 'pending' ? (
        <Space size="small" wrap>
          <ReleasePopover
            options={releaseOptions}
            loading={releaseMutation.isPending}
            onRelease={(mode) => releaseMutation.mutate({ poId: r.id, mode })}
          />
          <SetExecPricePopover onSet={(p) => setExecPriceMutation.mutate({ poId: r.id, price: p })} loading={setExecPriceMutation.isPending} />
          <Popconfirm title="确认取消该待执行订单？" onConfirm={() => cancelPendingMutation.mutate(r.id)}>
            <Button size="small" danger>取消</Button>
          </Popconfirm>
        </Space>
      ) : <Text type="secondary">{r.resulting_order_id ? `order ${r.resulting_order_id}` : '-'}</Text>,
    },
  ]

  const positionColumns: ColumnsType<any> = [
    { title: '交易对', dataIndex: 'symbol', key: 'symbol', width: 110 },
    { title: '方向', dataIndex: 'side', key: 'side', width: 70, render: (s) => <Tag color={s === 'long' ? 'green' : 'red'}>{s}</Tag> },
    { title: '数量', dataIndex: 'contracts', key: 'contracts', align: 'right' as const, render: (v) => String(v) },
    { title: '开仓价', dataIndex: 'entry_price', key: 'entry_price', align: 'right' as const, render: (v) => String(v) },
    { title: '标记价', dataIndex: 'mark_price', key: 'mark_price', align: 'right' as const, render: (v) => (v != null ? String(v) : '-') },
    { title: '仓位价值', dataIndex: 'position_value_usdt', key: 'position_value_usdt', align: 'right' as const, render: (v) => (v != null ? `${Number(v).toFixed(2)}` : '-') },
    { title: '未实现盈亏', dataIndex: 'unrealized_pnl', key: 'unrealized_pnl', align: 'right' as const, width: 110, render: (v) => {
      const n = toNumber(v, NaN)
      if (!Number.isFinite(n)) return '-'
      return <span style={{ color: pnlColor(n), fontWeight: 600 }}>{formatPnlUsd(n)}</span>
    } },
    {
      title: '限价平仓', key: 'limit_close', width: 130,
      render: (_, r) => r.limit_close_enabled ? (
        <Tag color="blue">挂单 {r.limit_close_price ? String(r.limit_close_price) : ''}</Tag>
      ) : <Tag>未启用</Tag>,
    },
    {
      title: '金额止损', key: 'stop_loss', width: 120,
      render: (_, r) => r.stop_loss_enabled ? (
        <Tag color="orange">≤{r.stop_loss_amount_usdt ? Number(r.stop_loss_amount_usdt).toFixed(2) : ''}U</Tag>
      ) : <Tag>未启用</Tag>,
    },
    {
      title: '操作', key: 'actions', width: 240, fixed: 'right' as const,
      render: (_, r) => (
        <Space size="small" wrap>
          <ClosePositionPopover
            loading={closePosMut.isPending}
            onClose={(pct) => closePosMut.mutate({ pid: r.id, data: { type: 'market', close_percentage: pct } })}
          />
          <LimitClosePopover
            enabled={!!r.limit_close_enabled}
            currentPrice={r.limit_close_price ? Number(r.limit_close_price) : undefined}
            loading={limitCloseMut.isPending}
            onApply={(enabled, price) => limitCloseMut.mutate({ pid: r.id, data: { enabled, price, token_id: tokenId, symbol: r.symbol, side: r.side, exchange: r.exchange } })}
          />
          <StopLossPopover
            enabled={!!r.stop_loss_enabled}
            currentAmount={r.stop_loss_amount_usdt ? Number(r.stop_loss_amount_usdt) : undefined}
            loading={stopLossMut.isPending}
            onApply={(enabled, amount) => stopLossMut.mutate({ pid: r.id, data: { enabled, amount_usdt: amount, token_id: tokenId, symbol: r.symbol, side: r.side, exchange: r.exchange } })}
          />
        </Space>
      ),
    },
  ]

  const openOrderColumns: ColumnsType<OpenOrderInfo> = [
    { title: '交易对', dataIndex: 'symbol', key: 'symbol' },
    { title: '方向', dataIndex: 'side', key: 'side', render: (s) => <Tag color={s === 'buy' ? 'green' : 'red'}>{s}</Tag> },
    { title: '类型', dataIndex: 'type', key: 'type' },
    { title: '价格', dataIndex: 'price', key: 'price', align: 'right' as const, render: (v) => String(v) },
    { title: '数量', dataIndex: 'amount', key: 'amount', align: 'right' as const, render: (v) => String(v) },
    { title: '已成交', dataIndex: 'filled', key: 'filled', align: 'right' as const, render: (v) => String(v) },
    { title: '状态', dataIndex: 'status', key: 'status' },
    { title: '操作', key: 'actions', width: 100, render: (_, r) => (
      <Popconfirm title="确认撤单？" onConfirm={() => cancelOpenOrderMutation.mutate({ orderId: r.order_id, symbol: String(r.symbol || '') })}>
        <Button size="small" danger>撤单</Button>
      </Popconfirm>
    ) },
  ]

  const eventColumns: ColumnsType<any> = [
    { title: '时间', dataIndex: 'occurred_at', key: 'occurred_at', width: 170, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-') },
    { title: '事件', dataIndex: 'event_type', key: 'event_type', width: 100, render: (v) => <Tag>{v}</Tag> },
    { title: '来源', dataIndex: 'source', key: 'source', width: 90 },
    { title: '交易对', dataIndex: 'symbol', key: 'symbol', width: 110 },
    { title: '方向', dataIndex: 'side', key: 'side', width: 60 },
    { title: '数量', dataIndex: 'contracts', key: 'contracts', align: 'right' as const, render: (v) => (v != null ? String(v) : '-') },
    { title: '价格', dataIndex: 'entry_price', key: 'entry_price', align: 'right' as const, render: (v) => (v != null ? String(v) : '-') },
    { title: '状态', dataIndex: 'status', key: 'status', width: 90 },
  ]

  const pendingActiveCount = pendingOrders?.filter((p) => p.status === 'pending').length ?? 0

  const mobileTabLabels = React.useMemo(
    () => [
      { key: 'positions', label: `持仓${positions && positions.length > 0 ? ` (${positions.length})` : ''}` },
      { key: 'pending', label: `待执行${pendingActiveCount > 0 ? ` (${pendingActiveCount})` : ''}` },
      { key: 'open-orders', label: `挂单${openOrders && openOrders.length > 0 ? ` (${openOrders.length})` : ''}` },
      { key: 'news', label: '快讯' },
      { key: 'market-sentiment', label: '情绪' },
      { key: 'config', label: '配置' },
      { key: 'exec-stats', label: '执行统计' },
      { key: 'logs', label: '日志' },
    ],
    [positions, pendingActiveCount, openOrders],
  )

  if (authMode === 'none') return <div style={pageWrap}><Card><Alert type="info" message="即将跳转登录页…" /></Card></div>
  if (tokenLoading) return <div style={pageWrap}><Skeleton active /></div>
  if (!token) return <div style={pageWrap}><Card><Text>Token 不存在</Text></Card></div>

  return (
    <div style={pageWrap}>
    <Card
      title={
        <div className="token-orders-header">
          <Title level={5} className="token-orders-header-title">
            订单管理：{token.name}
          </Title>
          {authMode === 'panel' ? (
            <Button size="small" onClick={logoutPanel}>退出登录</Button>
          ) : (
            <Button size="small" onClick={() => navigate('/tokens')}>返回</Button>
          )}
        </div>
      }
      bodyStyle={{ padding: isMobile ? '8px 4px' : undefined }}
    >
      <AccountLeverageBar data={acctLev} positions={positions} isMobile={isMobile} />
      <PendingNotifyControl
        supported={panelNotify.supported}
        enabled={panelNotify.notifyEnabled}
        permission={panelNotify.permission}
        onToggle={panelNotify.toggleNotify}
      />
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        animated={!isMobile}
        size={isMobile ? 'small' : 'middle'}
        renderTabBar={(props, DefaultTabBar) =>
          isMobile ? (
            <MobileOrderTabBar activeKey={activeTab} onChange={setActiveTab} tabs={mobileTabLabels} />
          ) : (
            <DefaultTabBar {...props} />
          )
        }
        items={[
          {
            key: 'positions',
            label: `持仓${positions && positions.length > 0 ? ` (${positions.length})` : ''}`,
            children: isMobile ? (
              <PositionsMobileList
                positions={positions || []}
                loading={posLoading}
                tokenId={tokenId}
                closePosMut={closePosMut}
                limitCloseMut={limitCloseMut}
                stopLossMut={stopLossMut}
              />
            ) : (
              <Table
                rowKey={(r) => `${r.id || `${r.token_id}-${r.symbol}-${r.side}`}`}
                size="small"
                loading={posLoading}
                dataSource={positions || []}
                columns={positionColumns}
                scroll={{ x: 1100 }}
                pagination={{ pageSize: 20 }}
              />
            ),
          },
          {
            key: 'pending',
            label: `待执行订单${pendingOrders && pendingOrders.filter((p) => p.status === 'pending').length > 0 ? ` (${pendingOrders.filter((p) => p.status === 'pending').length})` : ''}`,
            children: (
              <>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Text>显示历史：</Text>
                  <Switch checked={includeClosed} onChange={setIncludeClosed} />
                  <Text type="secondary">（开=含已取消/已执行/失败）</Text>
                </Space>
                <Table<PendingOrderInfo> rowKey="id" size="small" loading={pendingLoading} dataSource={pendingOrders || []} columns={pendingColumns} scroll={{ x: 1200 }} pagination={{ pageSize: 20 }} />
              </>
            ),
          },
          {
            key: 'open-orders',
            label: `挂单${openOrders && openOrders.length > 0 ? ` (${openOrders.length})` : ''}`,
            children: isMobile ? (
              <OpenOrdersMobileList
                orders={openOrders || []}
                loading={ooLoading}
                cancelLoading={cancelOpenOrderMutation.isPending}
                onCancel={(orderId, symbol) => cancelOpenOrderMutation.mutate({ orderId, symbol })}
              />
            ) : (
              <Table<OpenOrderInfo> rowKey="order_id" size="small" loading={ooLoading} dataSource={openOrders || []} columns={openOrderColumns} scroll={{ x: 760 }} pagination={{ pageSize: 20 }} />
            ),
          },
          {
            key: 'news',
            label: '快讯',
            children: (
              <NewsFlashTab
                enabled={activeTab === 'news' && authMode !== 'none'}
                isMobile={isMobile}
                fetchNews={authMode === 'panel' ? panelApi.getNewsFlash : api.news.getFlash}
              />
            ),
          },
          {
            key: 'market-sentiment',
            label: isMobile ? '情绪' : '市场情绪',
            children: (
              <MarketSentimentTab
                enabled={activeTab === 'market-sentiment' && authMode !== 'none'}
                isMobile={isMobile}
                fetchFearGreed={authMode === 'panel' ? panelApi.getFearGreed : api.market.getFearGreed}
                fetchEtfFlows={authMode === 'panel' ? panelApi.getEtfFlows : api.market.getEtfFlows}
              />
            ),
          },
          {
            key: 'config',
            label: '配置',
            children: (
              <Form
                form={configForm}
                layout="vertical"
                style={{ maxWidth: 560 }}
                initialValues={{
                  execution_mode: token.execution_mode || 'off',
                  block_open_enabled: !!token.block_open_enabled,
                  block_close_enabled: !!token.block_close_enabled,
                  block_coefficient: Math.min(
                    BLOCK_COEFFICIENT_MAX,
                    Math.max(BLOCK_COEFFICIENT_MIN, Number(token.block_coefficient ?? 1)),
                  ),
                  panel_username: token.panel_username || '',
                  panel_password: '',
                }}
                onValuesChange={(changed) => {
                  // 预设联动：选预设时一次性设置 execution_mode + 两个 block 开关
                  if (changed.preset) {
                    const p = changed.preset
                    if (p === 'immediate_market') configForm.setFieldsValue({ execution_mode: 'off', block_open_enabled: false, block_close_enabled: false })
                    else if (p === 'immediate_maker') configForm.setFieldsValue({ execution_mode: 'maker_market', block_open_enabled: false, block_close_enabled: false })
                    else if (p === 'immediate_offset') configForm.setFieldsValue({ execution_mode: 'limit_offset', block_open_enabled: false, block_close_enabled: false })
                    else if (p === 'block_open') configForm.setFieldsValue({ block_open_enabled: true, block_close_enabled: false })
                    else if (p === 'block_both') configForm.setFieldsValue({ block_open_enabled: true, block_close_enabled: true })
                  }
                }}
                onFinish={(v) => {
                  // 面板用户不可改凭证字段；admin 才发送 panel_*。
                  const payload: any = {
                    execution_mode: v.execution_mode,
                    block_open_enabled: v.block_open_enabled,
                    block_close_enabled: v.block_close_enabled,
                    block_coefficient: v.block_coefficient,
                  }
                  if (authMode === 'admin') {
                    payload.panel_username = v.panel_username || null
                    if (v.panel_password) payload.panel_password = v.panel_password
                  }
                  configMutation.mutate(payload)
                }}
              >
                <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="交易所">{token.exchange_name}</Descriptions.Item>
                  <Descriptions.Item label="当前执行模式">{token.execution_mode || 'off'}</Descriptions.Item>
                  {authMode === 'panel' && (
                    <Descriptions.Item label="面板用户">{token.panel_username || '-'}</Descriptions.Item>
                  )}
                </Descriptions>

                <Title level={5}>执行策略</Title>
                <Form.Item name="preset" label="快速预设" extra="一键套用常见组合；下方细项仍可单独调整">
                  <Select
                    options={[
                      { value: 'immediate_market', label: '立即市价（自动执行）' },
                      { value: 'immediate_maker', label: '立即市价转限价（自动，仅 Binance）' },
                      { value: 'immediate_offset', label: '立即偏移百分比限价（自动）' },
                      { value: 'block_open', label: '人工放行开仓（平仓仍自动）' },
                      { value: 'block_both', label: '人工放行开仓+平仓' },
                    ]}
                    placeholder="选择预设套用…"
                    allowClear
                  />
                </Form.Item>

                <Form.Item name="execution_mode" label="默认执行方式（market 信号）" extra="决定自动执行路径与「放行」时的可选方式：off=市价 / maker_market=市价转限价（仅Binance）/ limit_offset=偏移百分比限价">
                  <Select options={EXEC_MODE_OPTIONS} />
                </Form.Item>

                <Title level={5}>人工审核（阻塞）</Title>
                <Form.Item name="block_open_enabled" label="阻塞开仓市价单" valuePropName="checked" extra="开启后，webhook 开仓市价单不立即执行，落库为待执行，由你在面板放行">
                  <Switch />
                </Form.Item>
                <Form.Item name="block_close_enabled" label="阻塞平仓市价单" valuePropName="checked" extra="开启后，webhook 平仓市价单不立即执行，落库为待执行">
                  <Switch />
                </Form.Item>
                <Form.Item
                  name="block_coefficient"
                  label="待执行订单默认数量系数"
                  extra="实际数量 = 原始数量 × 系数（0～2，可小数）；每单仍可在待执行列表里单独修改"
                  rules={[
                    { required: true, message: '请输入系数' },
                    { type: 'number', min: BLOCK_COEFFICIENT_MIN, max: BLOCK_COEFFICIENT_MAX, message: '系数范围为 0～2' },
                  ]}
                >
                  <InputNumber min={BLOCK_COEFFICIENT_MIN} max={BLOCK_COEFFICIENT_MAX} step={0.01} style={{ width: 160 }} />
                </Form.Item>

                <SignalPathPreview form={configForm} />

                {authMode === 'admin' && (
                  <>
                    <Divider />
                    <Title level={5}>面板登录凭证（仅管理员可设置）</Title>
                    <Form.Item name="panel_username" label="面板用户名" extra="留空则不启用面板登录">
                      <Input placeholder="如 trader_a" />
                    </Form.Item>
                    <Form.Item name="panel_password" label="面板密码" extra="留空则不修改；首次设置时填入新密码">
                      <Input.Password placeholder="设置新密码（留空不修改）" />
                    </Form.Item>
                  </>
                )}
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={configMutation.isPending}>保存配置</Button>
                </Form.Item>
              </Form>
            ),
          },
          {
            key: 'exec-stats',
            label: '执行统计',
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space>
                  <Text>统计窗口：</Text>
                  <Select size="small" value={statsDays} onChange={setStatsDays}
                    options={[{value:1,label:'近1天'},{value:7,label:'近7天'},{value:30,label:'近30天'},{value:90,label:'近90天'}]}
                    style={{ width: 110 }} />
                </Space>
                {statsLoading ? <Skeleton active /> : (
                  <>
                    <Row gutter={[8,8]}>
                      <Col xs={12} sm={8} md={6}><Statistic title="订单总数" value={execStats?.total_orders ?? 0} /></Col>
                      <Col xs={12} sm={8} md={6}><Statistic title="maker 成功率" value={execStats?.maker_success_rate ?? '-'} suffix={execStats?.maker_success_rate!=null ? '%' : ''} /></Col>
                      <Col xs={12} sm={8} md={6}><Statistic title="maker 尝试次数" value={execStats?.maker_attempts ?? 0} /></Col>
                      <Col xs={12} sm={8} md={6}><Statistic title="平均滑点(bps)" value={execStats?.avg_slippage_bps ?? '-'} precision={2} /></Col>
                    </Row>
                    <Text strong>按执行模式</Text>
                    <Table size="small" rowKey="mode" pagination={false}
                      dataSource={execStats?.by_mode || []}
                      columns={[
                        { title: '模式', dataIndex: 'mode', key: 'mode' },
                        { title: '订单数', dataIndex: 'count', key: 'count', align: 'right' as const },
                        { title: '兜底次数', dataIndex: 'fallback_count', key: 'fallback_count', align: 'right' as const },
                        { title: '兜底率', dataIndex: 'fallback_rate', key: 'fallback_rate', align: 'right' as const, render: (v:any) => v!=null?`${v}%`:'-' },
                        { title: '平均滑点(bps)', dataIndex: 'avg_slippage_bps', key: 'avg_slippage_bps', align: 'right' as const, render: (v:any) => v!=null?Number(v).toFixed(2):'-' },
                      ]}
                    />
                    <Text strong>按成交结果</Text>
                    <Table size="small" rowKey={(r)=>String(r[0])} pagination={false}
                      dataSource={Object.entries(execStats?.by_outcome || {}).map(([k,v])=>({outcome:k,count:v}))}
                      columns={[
                        { title: '结果', dataIndex: 'outcome', key: 'outcome' },
                        { title: '次数', dataIndex: 'count', key: 'count', align: 'right' as const },
                      ]}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      maker 成功率 = maker_filled / (maker_filled + market_fallback)；滑点正值表示成交价劣于信号到达时市价。limit_offset 的 limit_filled 计为成功成交。
                    </Text>
                  </>
                )}
              </Space>
            ),
          },
          {
            key: 'logs',
            label: '日志',
            children: <Table rowKey="id" size="small" loading={logLoading} dataSource={tradeEvents || []} columns={eventColumns} scroll={{ x: 820 }} pagination={{ pageSize: 20 }} />,
          },
        ]}
      />
    </Card>
    </div>
  )
}

function AccountLeverageBar({ data, positions, isMobile }: { data: any; positions?: any[]; isMobile: boolean }) {
  const lev = data?.actual_leverage
  const levColor = lev == null ? '#1677ff' : lev >= 10 ? '#cf1322' : lev >= 5 ? '#fa8c16' : '#3f8600'
  const positionsUpl = React.useMemo(
    () => (positions || []).reduce((sum, row) => sum + toNumber(row.unrealized_pnl), 0),
    [positions],
  )
  const apiUpl = data?.unrealized_pnl_usdt
  const unrealizedPnl = React.useMemo(() => {
    if (positions && positions.length > 0) return positionsUpl
    if (apiUpl != null && Number.isFinite(Number(apiUpl))) return Number(apiUpl)
    return null
  }, [positions, positionsUpl, apiUpl])
  const uplNumeric = unrealizedPnl == null ? 0 : toNumber(unrealizedPnl)
  return (
    <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: isMobile ? '8px 12px' : '12px 16px' }}>
      <Row gutter={[8, 8]} align="middle">
        <Col xs={12} sm={6} md={5}>
          <Statistic
            title="实际杠杆"
            value={lev == null ? '-' : lev}
            precision={2}
            valueStyle={{ color: levColor, fontWeight: 600 }}
            suffix={lev == null ? '' : 'x'}
          />
        </Col>
        <Col xs={12} sm={6} md={5}>
          <Statistic title="账户权益(USDT)" value={data?.equity_usdt ?? '-'} precision={2} />
        </Col>
        <Col xs={12} sm={6} md={5}>
          <Statistic title="持仓名义(USDT)" value={data?.total_notional_usdt ?? '-'} precision={2} />
        </Col>
        <Col xs={12} sm={6} md={5}>
          <Statistic
            title="未实现盈亏(USDT)"
            value={unrealizedPnl == null ? '-' : uplNumeric}
            prefix={unrealizedPnl == null ? undefined : '$'}
            precision={unrealizedPnl == null ? undefined : 2}
            valueStyle={{ color: pnlColor(uplNumeric), fontWeight: 600 }}
          />
        </Col>
      </Row>
      <Text type="secondary" style={{ fontSize: 12 }}>实际杠杆 = 持仓总名义价值 / 账户权益（10秒刷新）</Text>
    </Card>
  )
}

function OpenOrdersMobileList({
  orders,
  loading,
  cancelLoading,
  onCancel,
}: {
  orders: OpenOrderInfo[]
  loading: boolean
  cancelLoading: boolean
  onCancel: (orderId: string, symbol: string) => void
}) {
  if (loading && orders.length === 0) return <Skeleton active />
  if (!orders.length) return <Empty description="暂无挂单" style={{ padding: '24px 0' }} />

  return (
    <div className="open-orders-card-list">
      {orders.map((r) => {
        const isBuy = String(r.side || '').toLowerCase() === 'buy'
        return (
          <Card
            key={r.order_id}
            size="small"
            className={`open-order-card ${isBuy ? 'buy' : 'sell'}`}
            styles={{ body: { padding: '10px 12px' } }}
          >
            <div className="position-card-header">
              <Space wrap size={4}>
                <Text strong style={{ fontSize: 14 }}>{formatSymbolShort(r.symbol)}</Text>
                <Tag color={isBuy ? 'green' : 'red'}>{sideLabel(r.side)}</Tag>
                <Tag>{r.type || 'limit'}</Tag>
              </Space>
              {r.status ? <Tag>{r.status}</Tag> : null}
            </div>
            <div className="position-card-grid">
              <div>
                <span className="position-card-label">价格</span>
                <span>{r.price != null ? String(r.price) : '-'}</span>
              </div>
              <div>
                <span className="position-card-label">数量</span>
                <span>{r.amount != null ? String(r.amount) : '-'}</span>
              </div>
              <div>
                <span className="position-card-label">已成交</span>
                <span>{r.filled != null ? String(r.filled) : '-'}</span>
              </div>
              <div>
                <span className="position-card-label">剩余</span>
                <span>{r.remaining != null ? String(r.remaining) : '-'}</span>
              </div>
              {r.datetime ? (
                <div className="position-card-span2">
                  <span className="position-card-label">时间</span>
                  <span style={{ fontSize: 12 }}>{dayjs(r.datetime).isValid() ? dayjs(r.datetime).format('MM-DD HH:mm') : r.datetime}</span>
                </div>
              ) : null}
            </div>
            <div className="open-order-card-actions">
              <Popconfirm
                title="确认撤单？"
                onConfirm={() => onCancel(r.order_id, String(r.symbol || ''))}
              >
                <Button size="small" danger block loading={cancelLoading}>
                  撤单
                </Button>
              </Popconfirm>
            </div>
          </Card>
        )
      })}
    </div>
  )
}

function PositionsMobileList({
  positions,
  loading,
  tokenId,
  closePosMut,
  limitCloseMut,
  stopLossMut,
}: {
  positions: any[]
  loading: boolean
  tokenId: number
  closePosMut: { isPending: boolean; mutate: (args: { pid: number; data: any }) => void }
  limitCloseMut: { isPending: boolean; mutate: (args: { pid: number; data: any }) => void }
  stopLossMut: { isPending: boolean; mutate: (args: { pid: number; data: any }) => void }
}) {
  if (loading && positions.length === 0) return <Skeleton active />
  if (!positions.length) return <Empty description="暂无持仓" style={{ padding: '24px 0' }} />

  return (
    <div className="positions-card-list">
      {positions.map((r) => {
        const upl = toNumber(r.unrealized_pnl, NaN)
        const uplClass = Number.isFinite(upl) ? (upl >= 0 ? 'profit' : 'loss') : ''
        return (
        <Card
          key={`${r.id || `${r.token_id}-${r.symbol}-${r.side}`}`}
          size="small"
          className={`position-card ${uplClass}`}
          styles={{ body: { padding: '10px 12px' } }}
        >
          <div className="position-card-header">
            <Space wrap size={4}>
              <Text strong style={{ fontSize: 14, wordBreak: 'break-all' }}>{formatSymbolShort(r.symbol)}</Text>
              <Tag color={r.side === 'long' ? 'green' : 'red'}>{r.side}</Tag>
            </Space>
            <div className="position-card-pnl" style={{ color: pnlColor(upl) }}>
              <span className="position-card-pnl-label">未实现盈亏</span>
              <span className="position-card-pnl-value">{formatPnlUsd(r.unrealized_pnl)}</span>
            </div>
          </div>
          <div className="position-card-grid">
            <div>
              <span className="position-card-label">数量</span>
              <span>{String(r.contracts ?? '-')}</span>
            </div>
            <div>
              <span className="position-card-label">开仓价</span>
              <span>{String(r.entry_price ?? '-')}</span>
            </div>
            <div>
              <span className="position-card-label">标记价</span>
              <span>{r.mark_price != null ? String(r.mark_price) : '-'}</span>
            </div>
            <div>
              <span className="position-card-label">仓位价值</span>
              <span>{r.position_value_usdt != null ? `$${Number(r.position_value_usdt).toFixed(2)}` : '-'}</span>
            </div>
            <div className="position-card-span2">
              <span className="position-card-label">限价平仓</span>
              <span>
                {r.limit_close_enabled ? (
                  <Tag color="blue">挂单 {r.limit_close_price ? String(r.limit_close_price) : ''}</Tag>
                ) : (
                  <Tag>未启用</Tag>
                )}
              </span>
            </div>
            <div className="position-card-span2">
              <span className="position-card-label">金额止损</span>
              <span>
                {r.stop_loss_enabled ? (
                  <Tag color="orange">≤{r.stop_loss_amount_usdt ? Number(r.stop_loss_amount_usdt).toFixed(2) : ''}U</Tag>
                ) : (
                  <Tag>未启用</Tag>
                )}
              </span>
            </div>
          </div>
          <div className="position-card-actions">
            <ClosePositionPopover
              loading={closePosMut.isPending}
              onClose={(pct) => closePosMut.mutate({ pid: r.id, data: { type: 'market', close_percentage: pct } })}
            />
            <LimitClosePopover
              enabled={!!r.limit_close_enabled}
              currentPrice={r.limit_close_price ? Number(r.limit_close_price) : undefined}
              loading={limitCloseMut.isPending}
              onApply={(enabled, price) =>
                limitCloseMut.mutate({
                  pid: r.id,
                  data: { enabled, price, token_id: tokenId, symbol: r.symbol, side: r.side, exchange: r.exchange },
                })
              }
            />
            <StopLossPopover
              enabled={!!r.stop_loss_enabled}
              currentAmount={r.stop_loss_amount_usdt ? Number(r.stop_loss_amount_usdt) : undefined}
              loading={stopLossMut.isPending}
              onApply={(enabled, amount) =>
                stopLossMut.mutate({
                  pid: r.id,
                  data: { enabled, amount_usdt: amount, token_id: tokenId, symbol: r.symbol, side: r.side, exchange: r.exchange },
                })
              }
            />
          </div>
        </Card>
        )
      })}
    </div>
  )
}

function ClosePositionPopover({ onClose, loading }: { onClose: (pct: number) => void; loading: boolean }) {
  const [open, setOpen] = useState(false)
  const [pct, setPct] = useState<number>(100)
  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) setPct(100) }}
      trigger="click"
      title="市价平仓"
      content={
        <Space direction="vertical" size="small" style={{ width: 220 }}>
          <Space>
            <Text>平仓比例：</Text>
            <InputNumber size="small" min={1} max={100} value={pct} onChange={(v) => setPct(Number(v) || 100)} style={{ width: 90 }} />
            <Text>%</Text>
          </Space>
          <Popconfirm title={`确认市价平仓 ${pct}%？`} onConfirm={() => { onClose(pct); setOpen(false) }}>
            <Button size="small" type="primary" danger block loading={loading}>确认平仓</Button>
          </Popconfirm>
        </Space>
      }
    >
      <Button size="small" danger>平仓</Button>
    </Popover>
  )
}

function LimitClosePopover({ enabled, currentPrice, onApply, loading }: {
  enabled: boolean; currentPrice?: number; onApply: (enabled: boolean, price?: number) => void; loading: boolean
}) {
  const [open, setOpen] = useState(false)
  const [price, setPrice] = useState<string>(currentPrice != null ? String(currentPrice) : '')
  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) setPrice(currentPrice != null ? String(currentPrice) : '') }}
      trigger="click"
      title="限价平仓"
      content={
        <Space direction="vertical" size="small" style={{ width: 220 }}>
          <Input size="small" placeholder="限价价格" value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: '100%' }} />
          <Space>
            <Button size="small" type="primary" loading={loading} onClick={() => { const p = Number(price); if (!Number.isNaN(p) && p > 0) { onApply(true, p); setOpen(false) } else message.warning('请输入正数价格') }}>启用</Button>
            {enabled && <Button size="small" loading={loading} onClick={() => { onApply(false); setOpen(false) }}>关闭</Button>}
          </Space>
        </Space>
      }
    >
      <Button size="small">限价平仓</Button>
    </Popover>
  )
}

function StopLossPopover({ enabled, currentAmount, onApply, loading }: {
  enabled: boolean; currentAmount?: number; onApply: (enabled: boolean, amount?: number) => void; loading: boolean
}) {
  const [open, setOpen] = useState(false)
  const [amt, setAmt] = useState<string>(currentAmount != null ? String(currentAmount) : '')
  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) setAmt(currentAmount != null ? String(currentAmount) : '') }}
      trigger="click"
      title="金额止损（最大可承受浮亏 USDT）"
      content={
        <Space direction="vertical" size="small" style={{ width: 220 }}>
          <Input size="small" placeholder="金额 USDT" value={amt} onChange={(e) => setAmt(e.target.value)} style={{ width: '100%' }} />
          <Space>
            <Button size="small" type="primary" loading={loading} onClick={() => { const a = Number(amt); if (!Number.isNaN(a) && a > 0) { onApply(true, a); setOpen(false) } else message.warning('请输入正数金额') }}>启用</Button>
            {enabled && <Button size="small" loading={loading} onClick={() => { onApply(false); setOpen(false) }}>关闭</Button>}
          </Space>
        </Space>
      }
    >
      <Button size="small">止损</Button>
    </Popover>
  )
}

function SignalPathPreview({ form }: { form: any }) {
  const executionMode = Form.useWatch('execution_mode', form) || 'off'
  const blockOpen = Form.useWatch('block_open_enabled', form)
  const blockClose = Form.useWatch('block_close_enabled', form)
  const modeLabel: Record<string, string> = { off: '市价', maker_market: '市价转限价', limit_offset: '偏移百分比限价' }
  const releaseModes = ['市价']
  if (executionMode === 'maker_market') releaseModes.push('市价转限价')
  if (executionMode === 'limit_offset') releaseModes.push('偏移百分比限价')
  const blocked = blockOpen || blockClose
  const lines: string[] = []
  lines.push(`市价信号到达 →`)
  if (blocked) {
    lines.push(`  开仓${blockOpen ? '阻塞→落库待放行' : '自动执行'}；平仓${blockClose ? '阻塞→落库待放行' : '自动执行'}`)
    lines.push(`  放行可选：${releaseModes.join(' / ')}；也可设执行价自动挂限价`)
  } else {
    lines.push(`  自动按「${modeLabel[executionMode] || '市价'}」执行`)
  }
  lines.push(`  超时/未成交 → 兜底市价单（平仓始终兜底）`)
  return (
    <Alert
      type="info"
      showIcon
      style={{ marginTop: 8, marginBottom: 8, whiteSpace: 'pre-line', fontSize: 13 }}
      message="信号路径预览"
      description={lines.join('\n')}
    />
  )
}

function SetExecPricePopover({ onSet, loading }: { onSet: (price: number) => void; loading: boolean }) {
  const [open, setOpen] = useState(false)
  const [val, setVal] = useState<string>('')
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      content={
        <Space>
          <Input placeholder="执行价" value={val} onChange={(e) => setVal(e.target.value)}
            onPressEnter={() => { const n = Number(val); if (!Number.isNaN(n) && n > 0) { onSet(n); setVal(''); setOpen(false) } }}
            style={{ width: 120 }} />
          <Button size="small" type="primary" loading={loading} onClick={() => {
            const n = Number(val)
            if (!Number.isNaN(n) && n > 0) { onSet(n); setVal(''); setOpen(false) } else message.warning('请输入正数')
          }}>挂限价</Button>
        </Space>
      }
      title="设置执行价（立即挂 GTC 限价单）"
    >
      <Button size="small">设执行价</Button>
    </Popover>
  )
}

function ReleasePopover({
  options,
  loading,
  onRelease,
}: {
  options: { value: string; label: string }[]
  loading: boolean
  onRelease: (mode: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<string>(options[0]?.value || 'market')
  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) setMode(options[0]?.value || 'market') }}
      trigger="click"
      title="确认放行"
      content={
        <Space direction="vertical" size="small" style={{ width: 220 }}>
          <Select size="small" style={{ width: '100%' }} value={mode} onChange={setMode} options={options} />
          <Button size="small" type="primary" block loading={loading} onClick={() => { onRelease(mode); setOpen(false) }}>
            确认放行
          </Button>
        </Space>
      }
    >
      <Button size="small" type="primary">放行</Button>
    </Popover>
  )
}

function PendingNotifyControl({
  supported,
  enabled,
  permission,
  onToggle,
}: {
  supported: boolean
  enabled: boolean
  permission: NotificationPermission | 'unsupported'
  onToggle: (next: boolean) => Promise<boolean>
}) {
  const [loading, setLoading] = useState(false)

  if (!supported) return null

  const handleChange = async (checked: boolean) => {
    setLoading(true)
    const ok = await onToggle(checked)
    setLoading(false)
    if (checked && !ok) {
      message.warning('未获得浏览器通知权限，请在浏览器设置中允许本网站发送通知')
    }
  }

  return (
    <Space wrap size="small" style={{ marginBottom: 12, marginTop: 4 }}>
      <BellOutlined />
      <Text>浏览器通知</Text>
      <Switch
        checked={enabled}
        loading={loading}
        onChange={handleChange}
        checkedChildren="开"
        unCheckedChildren="关"
      />
      {permission === 'denied' && (
        <Text type="danger">通知已被浏览器屏蔽，请在地址栏或系统设置中允许</Text>
      )}
      {enabled && (
        <Text type="secondary">自动成交或待执行单会推送；点击通知可跳转对应页面</Text>
      )}
    </Space>
  )
}
